import React from 'react';
import * as stylex from '@stylexjs/stylex';
import { useButton, useHover, mergeProps } from 'react-aria';
import '../theme/Colors.stylex';
import { ColorNeutral } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Padding } from '../theme/Spaces.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';
import { Icon, type IconName } from '../images-and-icons/Icon';

// MARK: exported types
export type Size = 'tiny' | 'small' | 'medium' | 'large';

// MARK: root
// ==========
type State = 'default' | 'hovered' | 'pressed';
type IconButtonProps = {
  iconName: IconName;
  iconWidth: number;
  iconHeight: number;
  size?: Size;
  accent?: boolean;
  active?: boolean;
  onPress: () => void;
};
function IconButtonComponent({
  iconName,
  iconWidth,
  iconHeight,
  size = 'small',
  accent = false,
  active = false,
  onPress
}: IconButtonProps) {
  const ref = React.useRef<HTMLButtonElement>(null);
  const {
    buttonProps,
    isPressed
  } = useButton({
    onPress
  }, ref);
  const {
    hoverProps,
    isHovered
  } = useHover({});
  let state: State;
  switch (true) {
    case isPressed:
      state = 'pressed';
      break;
    case isHovered:
      state = 'hovered';
      break;
    default:
      state = 'default';
      break;
  }
  const interactionProps = mergeProps(buttonProps, hoverProps);
  return <button ref={ref} {...interactionProps} {...styleProps(state, size, accent, active)}>
      <Icon name={iconName} width={iconWidth} height={iconHeight} fill={iconColorFromParams(state, accent, active)} bold />
    </button>;
}
const MemoizedIconButtonComponent = React.memo(IconButtonComponent);
export { MemoizedIconButtonComponent as CanaryIconButton };
export type { IconButtonProps };

// MARK: style:size
const sizeStyles = {
  tiny: {
    "padding-kmVPX3": "padding-x7l32zc",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:79"
  },
  small: {
    "padding-kmVPX3": "padding-x170jadj",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:80"
  },
  medium: {
    "padding-kmVPX3": "padding-xb4tss4",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:81"
  },
  large: {
    "padding-kmVPX3": "padding-xzp7fdy",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:82"
  }
};

// MARK: style:backgroundColor
const bgStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-xjbqb8w",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:87"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:88"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x1y0x0bx",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:89"
  }
};
const bgWithAccentStyles = {
  default: {
    "backgroundColor-kWkggS": "backgroundColor-xjbqb8w",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:93"
  },
  hovered: {
    "backgroundColor-kWkggS": "backgroundColor-x1y0x0bx",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:94"
  },
  pressed: {
    "backgroundColor-kWkggS": "backgroundColor-xct4vau",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:95"
  }
};

// MARK: style:borderColor
const borderStyles = {
  default: {
    "borderColor-kVAM5u": "borderColor-x8u4suw",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:100"
  },
  hovered: {
    "borderColor-kVAM5u": "borderColor-xlzvxj1",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:101"
  },
  pressed: {
    "borderColor-kVAM5u": "borderColor-x1lq5qq1",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:102"
  }
};
const borderWithAccentStyles = {
  default: {
    "borderColor-kVAM5u": "borderColor-x5bw2fz",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:106"
  },
  hovered: {
    "borderColor-kVAM5u": "borderColor-x1lq5qq1",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:107"
  },
  pressed: {
    "borderColor-kVAM5u": "borderColor-x1c9wgzy",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:108"
  }
};

// MARK: style:Icon
// ================
const iconColorFromParams = (state: State, accent: boolean, active: boolean) => {
  if (active) {
    switch (state) {
      case 'default':
        return ColorNeutral.iconAccent;
      case 'hovered':
        return ColorNeutral.iconAccentHover;
      case 'pressed':
        return ColorNeutral.iconPressed;
    }
  }
  switch (state) {
    case 'default':
      return ColorNeutral.iconDefault;
    case 'hovered':
      return accent ? ColorNeutral.iconAccentHover : ColorNeutral.iconAccent;
    case 'pressed':
      return ColorNeutral.iconAccentHover;
  }
};

// MARK: style
// ===========
const stylesBySize = () => sizeStyles;
const bgStylesByState = (accent: boolean) => accent ? bgWithAccentStyles : bgStyles;
const borderStylesByState = (accent: boolean) => accent ? borderWithAccentStyles : borderStyles;
const styleProps = (state: State, size: Size, accent: boolean, active: boolean) => {
  return stylex.props(styles.root, stylesBySize()[size], bgStylesByState(accent)[state], active && borderStylesByState(accent)[state], active && styles.root_bordered);
};
const styles = {
  root: {
    "display-k1xSpc": "display-x78zum5",
    "flexDirection-kXwgrk": "flexDirection-xdt5ytf",
    "borderRadius-kaIpWk": "borderRadius-x1qd5jr3",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:154"
  },
  root_bordered: {
    "borderWidth-kMzoRj": "borderWidth-xmkeg23",
    "borderStyle-ksu8eU": "borderStyle-x1y0btm7",
    $$css: "wino-gc:src/resources/actions/CanaryIconButton.tsx:159"
  }
};